<script setup lang="ts">
import useEmblaCarousel from 'embla-carousel-vue'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { motion } from 'motion-v'
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

const [emblaRef, emblaApi] = useEmblaCarousel({
  align: 'start',
  slidesToScroll: 'auto',
  loop: false,
}, [
  WheelGesturesPlugin(),
])

const { selectedIndex, scrollSnaps, handleClick } = useEmblaDots(emblaApi)
const { isPrevDisabled, isNextDisabled, handlePrevious, handleNext }
  = useEmblaButtons(emblaApi)

function getStaticAsset(index: StringifiedInt<IntRange<0, 2>>) {
  return match(index)
    .with('0', () => ({
      image: `${S3_ASSETS_URL}/home/video-testimonials/kevin-gillespie.jpg`,
      video: 'https://static.spotapps.co/website_videos/Kevin%20Gillespie%2C%20Red%20Beard%20Restaurant%20Group%20%26%20Spothopper-1.m4v',
    }))
    .with('1', () => ({
      image: `${S3_ASSETS_URL}/home/video-testimonials/hieu.jpg`,
      video: 'https://static.spotapps.co/website_videos/Crawfish%20Shack%20Seafood%2C%20Crawfish%20Shack%20Seafood%20%26%20Bar%2C%20And%20Spothopper-1.m4v',
    }))
    .exhaustive()
}
</script>

<template>
  <section
    class="mx-auto max-w-screen-xl overflow-x-clip bg-white px-4 py-8 md:bg-transparent lg:px-8 lg:py-16"
  >
    <header class="grid place-items-center gap-y-5">
      <UiSectionHeadline
        :title="t('home.section.video_testimonials.title')"
        :subtitle="t('home.section.video_testimonials.subtitle')"
      />

      <UiButton
        class="group w-full max-w-xs lg:max-w-max"
        color="accent"
        as-child
      >
        <RouterLink :to="{ name: '/free-demo' }">
          {{ t("home.section.video_testimonials.action") }}

          <UiIcon
            name="arrow-forward-rounded"
            class="transition-transform duration-200 group-hover:translate-x-1"
          />
        </RouterLink>
      </UiButton>
    </header>

    <main class="mt-8">
      <div
        ref="emblaRef"
        class="relative select-none"
      >
        <div class="flex gap-x-5 [backface-visibility:hidden]">
          <div
            v-for="(item, itemIndex) in tm(
              'home.section.video_testimonials.items',
            )"
            :key="itemIndex"
            class="flex w-full shrink-0 grow-0 basis-[calc(100%-1.25rem*2)] flex-col justify-stretch overflow-clip rounded-2xl border border-gray-200 bg-white shadow-xl shadow-black/5 lg:basis-[calc((100%-1.25rem*2)/2)]"
          >
            <DialogRoot>
              <DialogTrigger as-child>
                <motion.div
                  role="button"
                  class="relative flex h-80 min-w-0 cursor-pointer overflow-clip rounded-s-2xl rounded-br-2xl"
                  :layout-id="`video-${itemIndex}`"
                >
                  <UiImageWithOverlay
                    :src="getStaticAsset(itemIndex).image"
                    container-class="!absolute inset-0"
                    class="aspect-video size-full object-cover object-top"
                    role="presentation"
                  />

                  <div
                    class="absolute inset-0 z-10 grid place-content-center"
                  >
                    <div
                      class="relative grid size-16 place-content-center before:absolute before:-inset-2 before:rounded-[50%] before:bg-gray-800/30 before:backdrop-blur-sm after:absolute after:inset-0 after:rounded-[50%] after:bg-gray-800/70 after:backdrop-blur-sm"
                    >
                      <UiIcon
                        name="play"
                        class="z-[1] text-2xl text-white"
                      />
                    </div>
                  </div>

                  <div
                    class="relative isolate grid select-none place-content-end p-5"
                  >
                    <span
                      class="flex text-2xl font-medium text-white"
                    >
                      {{ rt(item.name) }}
                    </span>

                    <span
                      class="flex text-lg text-white/80"
                    >
                      {{ rt(item.role) }}
                    </span>
                  </div>
                </motion.div>
              </DialogTrigger>

              <DialogPortal>
                <DialogOverlay
                  class="fixed inset-0 z-[100] bg-gray-800/80"
                  as-child
                >
                  <motion.div
                    :variants="dialogBackdrop"
                    initial="initial"
                    animate="enter"
                    exit="initial"
                  />
                </DialogOverlay>

                <DialogContent
                  class="fixed left-1/2 top-1/2 z-[200] max-w-screen-lg -translate-x-1/2 -translate-y-1/2 p-5 outline-none"
                >
                  <motion.div
                    :layout-id="`video-${itemIndex}`"
                    class="w-[min(90vw,64rem)] overflow-clip rounded-3xl shadow-2xl"
                  >
                    <video
                      :src="
                        getStaticAsset(itemIndex).video
                      "
                      autoplay
                      playsinline
                      controls
                      class="aspect-video size-full focus-visible:outline-none"
                    />
                  </motion.div>
                </DialogContent>
              </DialogPortal>
            </DialogRoot>

            <div
              class="text-balance p-5 text-center text-xl font-semibold before:inline-block before:content-['“'] after:inline-block after:content-['”']"
            >
              {{ rt(item.comment) }}
            </div>
          </div>
        </div>
      </div>

      <nav
        v-show="scrollSnaps.length > 1"
        class="mt-5 flex items-center justify-between gap-x-5"
      >
        <div class="hidden gap-x-2 sm:flex">
          <button
            v-for="(_, index) in scrollSnaps"
            :key="index"
            type="button"
            :class="[
              'size-3 rounded-[50%]',
              selectedIndex === index
                ? 'bg-orange-500'
                : 'bg-gray-200',
            ]"
            @click="handleClick(index)"
          />
        </div>

        <div class="ml-auto flex gap-x-2">
          <button
            type="button"
            class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
            :disabled="isPrevDisabled"
            @click="handlePrevious"
          >
            <UiIcon
              name="arrow-forward-rounded"
              class="-rotate-180"
            />
          </button>

          <button
            type="button"
            class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
            :disabled="isNextDisabled"
            @click="handleNext"
          >
            <UiIcon name="arrow-forward-rounded" />
          </button>
        </div>
      </nav>
    </main>
  </section>
</template>
